import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'

import Campaigns from '../components/campaigns/Campaigns'
import {
  changeCampaignState,
  changeCampaignListState,
  createCampaign,
  getCampaigns,
  resetCampaignState,
  toggleCampaign,
  resetCampaignsList,
  selectCampaign,
  downloadCampaignsCSV
} from '../store/actions/campaign'
import {
  DEFAULT_CONTAINER_STATE,
  CAMPAIGN_PROCESS_STEP,
  ANALYTICS_PROCESS_STEP
} from '../constants/containerStates'
import CreateCampaignForm from '../components/campaigns/CreateCampaignForm'
import { checkValidations } from '../util/validation.helpers'
import { containsValues } from '../util/object.helpers'
import {
  buildCampaignValidationRules,
  CAMPAIGN_STATUS_OPTIONS,
  campaignTableHeaders
} from '../util/campaign.helpers'
import { CAMPAIGN_PROFILE_DISPATCH } from '../store/reducers/campaign/campaign'
import { buildTableHeaders } from '../util/csv.helpers'
import { getTokens } from '../store/actions/token'
import { isDashboardReady } from '../util/dashboard.helpers'
import { isTokenFullIntegration } from '../util/analytics/pointsBank.helpers'
import ConfirmedPopup from '../components/popups/common/Confirmed'
import { isAPICompleted, isAPIInProgress } from '../util/app/app.helpers'

const CampaignsContainer = ({
  tokenProfile,
  dashboardSetupState,
  changeCampaignState,
  changeCampaignListState,
  createCampaign,
  resetCampaignState,
  getCampaigns,
  campaignListProfile,
  campaignProfile,
  toggleCampaign,
  resetCampaignsList,
  selectCampaign,
  getTokens,
  downloadProfile,
  downloadCampaignsCSV,
  brandProfile,
  ...restProps
}) => {
  const isDashboardSetupReady = isDashboardReady(dashboardSetupState)
  const isFullIntegration = isTokenFullIntegration(tokenProfile.type)

  const { t } = useTranslation()
  const [popupState, setPopupState] = useState({
    id: DEFAULT_CONTAINER_STATE.NONE,
    data: null
  })

  useEffect(() => {
    getTokens()
    return () => {
      resetCampaignsList()
    }
  }, [])

  useEffect(() => {
    if (isDashboardSetupReady && isFullIntegration) {
      getCampaigns({ pageNumber: 0 })
    }
  }, [isDashboardSetupReady, isFullIntegration])

  useEffect(() => {
    if (campaignProfile.saveSuccess) {
      handleNoPopup()
    }
  }, [campaignProfile.saveSuccess])

  useEffect(() => {
    const downloadEmailProgress = downloadProfile.downloadEmailProgress
    if (isAPIInProgress(downloadEmailProgress)) {
      setPopupState({ id: ANALYTICS_PROCESS_STEP.SENDING_REPORT_EMAIL })
    }
  }, [downloadProfile.downloadEmailProgress])

  const handleCreateNewCampaign = () => {
    setPopupState({
      id: CAMPAIGN_PROCESS_STEP.CREATE_NEW
    })
  }

  const handleNoPopup = () => {
    resetCampaignState()
    setPopupState({
      id: DEFAULT_CONTAINER_STATE.NONE
    })
    selectCampaign(null)
  }

  const handleActivateCampaign = (option, isActivate) => {
    selectCampaign(option, true)
    toggleCampaign(option.id, {
      status: isActivate
        ? CAMPAIGN_STATUS_OPTIONS.ACTIVE
        : CAMPAIGN_STATUS_OPTIONS.INACTIVE
    })
  }

  const handleChangeCampaignsPage = (pageNumber) => {
    getCampaigns({ pageNumber })
  }

  const handleCampaignsPerPageChange = (recordsPerPage) => {
    getCampaigns({ pageNumber: 0, recordsPerPage })
  }

  const handleCSVDownload = (option) => {
    downloadCampaignsCSV(option)
  }

  const handleOnCancelPopup = () => {
    switch (popupState) {
      default:
        handleNoPopup()
    }
  }

  const handleOnSubmitPopup = () => {
    switch (popupState.id) {
      case CAMPAIGN_PROCESS_STEP.CREATE_NEW:
        changeCampaignState(CAMPAIGN_PROFILE_DISPATCH.ERROR, {})
        const errors = checkValidations(
          buildCampaignValidationRules(campaignProfile)
        )
        if (containsValues(errors)) {
          changeCampaignState(CAMPAIGN_PROFILE_DISPATCH.ERROR, errors)
        } else {
          createCampaign()
        }
        break
      default:
        break
    }
  }

  const renderPopup = () => {
    switch (popupState.id) {
      case CAMPAIGN_PROCESS_STEP.CREATE_NEW:
        return (
          <CreateCampaignForm
            t={t}
            tokenProfile={tokenProfile}
            campaignProfile={campaignProfile}
            onClose={handleOnCancelPopup}
            onSubmit={handleOnSubmitPopup}
            dispatchAction={changeCampaignState}
            title={t('campaign.create-new-campaign')}
            submitLabel={t('campaign.create-campaign')}
          />
        )

      case ANALYTICS_PROCESS_STEP.SENDING_REPORT_EMAIL:
        return (
          <ConfirmedPopup
            t={t}
            onSubmit={handleNoPopup}
            title='analytics.csv-file-delivery'
            description='analytics.csv-file-delivery-desc'
            isProcessing={
              !isAPICompleted(downloadProfile.downloadEmailProgress)
            }
          />
        )

      default:
        break
    }
  }

  return (
    <>
      <Campaigns
        {...restProps}
        t={t}
        tokenProfile={tokenProfile}
        campaignListProfile={campaignListProfile}
        isDashboardSetupReady={isDashboardSetupReady}
        dispatchAction={changeCampaignListState}
        onCreateNew={handleCreateNewCampaign}
        campaignTableProps={{
          onChangeCampaignsPage: handleChangeCampaignsPage,
          onCampaignsPerPageChange: handleCampaignsPerPageChange,
          tableHeaders: buildTableHeaders(
            t,
            campaignTableHeaders(tokenProfile.symbol)
          ),
          onActivate: handleActivateCampaign,
          onCSVDownload: handleCSVDownload,
          loyaltyProgramUrl: brandProfile?.brandConfig?.loyaltyProgramUrl
        }}
        isFullIntegration={isFullIntegration}
      />
      {renderPopup()}
    </>
  )
}

const mapStateToProps = ({
  mainReducer,
  dashboardReducer,
  tokensReducer,
  campaignReducer
}) => {
  const { dashboardSetupProfile, brandProfile } = dashboardReducer
  const { tokenProfile } = tokensReducer
  const { campaignProfile, campaignListProfile } = campaignReducer
  const { downloadProfile } = mainReducer
  return {
    dashboardSetupState: dashboardSetupProfile.dashboardSetupState,
    tokenProfile,
    campaignListProfile,
    campaignProfile,
    downloadProfile,
    brandProfile
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getTokens,
      changeCampaignState,
      changeCampaignListState,
      createCampaign,
      resetCampaignState,
      getCampaigns,
      toggleCampaign,
      resetCampaignsList,
      selectCampaign,
      downloadCampaignsCSV
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(CampaignsContainer)
